import { useState } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { speseApi, commesseApi } from '../services/api'
import { Plus, Upload, CheckCircle, XCircle, CreditCard, Trash2, FileText } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import clsx from 'clsx'

const STATUS_CLS: Record<string, string> = {
  inviata: 'bg-blue-100 text-blue-700',
  approvata: 'bg-green-100 text-green-700',
  rifiutata: 'bg-red-100 text-red-700',
  pagata: 'bg-gray-100 text-gray-600',
}
const STATUS_LABEL: Record<string, string> = {
  inviata: 'Inviata',
  approvata: 'Approvata',
  rifiutata: 'Rifiutata',
  pagata: 'Pagata',
}

interface TipoSpesa { id: number; nome: string; categoria?: string }
interface Commessa { id: number; numero_commessa: string; nome: string }
interface Spesa {
  id: number
  tecnico_id: number
  commessa_id: number
  tipo_spesa_id: number
  importo: number
  data_spesa: string
  descrizione?: string
  metodo_pagamento: string
  receipt_file_path?: string
  status: string
  motivo_rifiuto?: string
  tecnico_nome?: string
  commessa_nome?: string
  tipo_spesa_nome?: string
}

const emptyForm = {
  commessa_id: '',
  tipo_spesa_id: '',
  importo: '',
  data_spesa: new Date().toISOString().split('T')[0],
  descrizione: '',
  metodo_pagamento: 'personale',
}

export default function SpeseRimborso() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = ['admin', 'superadmin', 'responsabile_tecnico', 'contabilita'].includes(user?.role || '')
  const isTecnico = ['tecnico', 'tecnico_senior'].includes(user?.role || '')

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [filterStatus, setFilterStatus] = useState('')
  const [uploadingId, setUploadingId] = useState<number | null>(null)
  const [rifiutoId, setRifiutoId] = useState<number | null>(null)
  const [motivoRifiuto, setMotivoRifiuto] = useState('')

  const { data: tipi = [] } = useQuery<TipoSpesa[]>({
    queryKey: ['tipi-spesa'],
    queryFn: () => speseApi.tipi().then(r => r.data),
  })

  const { data: commesse = [] } = useQuery<Commessa[]>({
    queryKey: ['commesse-select'],
    queryFn: () => commesseApi.list({ status: 'in_corso' }).then(r => r.data),
  })

  const { data: spese = [], isLoading } = useQuery<Spesa[]>({
    queryKey: ['spese', filterStatus],
    queryFn: () => speseApi.list({ status: filterStatus || undefined }).then(r => r.data),
  })

  const createMut = useMutation({
    mutationFn: (data: typeof emptyForm) =>
      speseApi.create({
        ...data,
        commessa_id: parseInt(data.commessa_id),
        tipo_spesa_id: parseInt(data.tipo_spesa_id),
        importo: parseFloat(data.importo),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['spese'] })
      toast.success('Spesa creata')
      setShowForm(false)
      setForm(emptyForm)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore creazione'),
  })

  const approvaMut = useMutation({
    mutationFn: (id: number) => speseApi.approva(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['spese'] }); toast.success('Spesa approvata') },
    onError: () => toast.error('Errore approvazione'),
  })

  const rifiutaMut = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => speseApi.rifiuta(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['spese'] })
      toast.success('Spesa rifiutata')
      setRifiutoId(null)
      setMotivoRifiuto('')
    },
    onError: () => toast.error('Errore rifiuto'),
  })

  const pagaMut = useMutation({
    mutationFn: (id: number) => speseApi.segnaPagata(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['spese'] }); toast.success('Spesa segnata come pagata') },
    onError: () => toast.error('Errore aggiornamento'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => speseApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['spese'] }); toast.success('Spesa eliminata') },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore eliminazione'),
  })

  const handleUploadRicevuta = async (id: number, file: File) => {
    setUploadingId(id)
    try {
      await speseApi.uploadRicevuta(id, file)
      qc.invalidateQueries({ queryKey: ['spese'] })
      toast.success('Ricevuta caricata')
    } catch {
      toast.error('Errore upload')
    } finally {
      setUploadingId(null)
    }
  }

  const totale = spese.reduce((sum, s) => sum + s.importo, 0)
  const totaleApprovate = spese.filter(s => ['approvata', 'pagata'].includes(s.status))
    .reduce((sum, s) => sum + s.importo, 0)

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {ConfirmModal}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CreditCard size={28} className="text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-800">Spese & Rimborsi</h1>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="input text-sm py-1"
          >
            <option value="">Tutti gli stati</option>
            <option value="inviata">Inviata</option>
            <option value="approvata">Approvata</option>
            <option value="rifiutata">Rifiutata</option>
            <option value="pagata">Pagata</option>
          </select>
          {isTecnico && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={16} /> Nuova Spesa
            </button>
          )}
        </div>
      </div>

      {/* Totali */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card text-center">
          <p className="text-sm text-gray-500">Totale Spese</p>
          <p className="text-2xl font-bold text-gray-800">€{totale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="card text-center">
          <p className="text-sm text-gray-500">Approvate/Pagate</p>
          <p className="text-2xl font-bold text-green-600">€{totaleApprovate.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="card text-center">
          <p className="text-sm text-gray-500">In Attesa</p>
          <p className="text-2xl font-bold text-blue-600">
            {spese.filter(s => s.status === 'inviata').length} voci
          </p>
        </div>
      </div>

      {/* Form nuova spesa */}
      {showForm && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Nuova Spesa</h2>
          <form
            onSubmit={e => { e.preventDefault(); createMut.mutate(form) }}
            className="grid grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-sm font-medium mb-1">Commessa *</label>
              <select
                className="input w-full"
                value={form.commessa_id}
                onChange={e => setForm(p => ({ ...p, commessa_id: e.target.value }))}
                required
              >
                <option value="">Seleziona commessa</option>
                {commesse.map(c => (
                  <option key={c.id} value={c.id}>{c.numero_commessa} – {c.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Tipo Spesa *</label>
              <select
                className="input w-full"
                value={form.tipo_spesa_id}
                onChange={e => setForm(p => ({ ...p, tipo_spesa_id: e.target.value }))}
                required
              >
                <option value="">Seleziona tipo</option>
                {tipi.map(t => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Importo (€) *</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                className="input w-full"
                value={form.importo}
                onChange={e => setForm(p => ({ ...p, importo: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data Spesa *</label>
              <input
                type="date"
                className="input w-full"
                value={form.data_spesa}
                onChange={e => setForm(p => ({ ...p, data_spesa: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Metodo Pagamento</label>
              <select
                className="input w-full"
                value={form.metodo_pagamento}
                onChange={e => setForm(p => ({ ...p, metodo_pagamento: e.target.value }))}
              >
                <option value="personale">Personale</option>
                <option value="carta_aziendale">Carta Aziendale</option>
                <option value="voucher">Voucher</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Descrizione</label>
              <input
                className="input w-full"
                value={form.descrizione}
                onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))}
                placeholder="Nota facoltativa"
              />
            </div>
            <div className="col-span-2 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button type="submit" className="btn-primary" disabled={createMut.isPending}>Crea Spesa</button>
            </div>
          </form>
        </div>
      )}

      {/* Dialog rifiuto */}
      {rifiutoId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="card w-96">
            <h3 className="text-lg font-semibold mb-3">Motivo Rifiuto</h3>
            <textarea
              className="input w-full h-24 mb-4"
              placeholder="Inserisci il motivo del rifiuto..."
              value={motivoRifiuto}
              onChange={e => setMotivoRifiuto(e.target.value)}
            />
            <div className="flex gap-3 justify-end">
              <button className="btn-secondary" onClick={() => { setRifiutoId(null); setMotivoRifiuto('') }}>
                Annulla
              </button>
              <button
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm"
                disabled={!motivoRifiuto.trim() || rifiutaMut.isPending}
                onClick={() => rifiutaMut.mutate({ id: rifiutoId, motivo: motivoRifiuto })}
              >
                Rifiuta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista spese */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Caricamento...</div>
      ) : spese.length === 0 ? (
        <div className="card text-center py-12 text-gray-500">Nessuna spesa trovata</div>
      ) : (
        <div className="space-y-3">
          {spese.map(s => (
            <div key={s.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`badge text-xs ${STATUS_CLS[s.status] || ''}`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                    <span className="font-semibold text-gray-800">
                      €{s.importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </span>
                    <span className="text-sm text-gray-500">
                      {format(new Date(s.data_spesa), 'dd/MM/yyyy', { locale: it })}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 flex flex-wrap gap-x-4">
                    {s.tecnico_nome && <span>Tecnico: {s.tecnico_nome}</span>}
                    {s.commessa_nome && <span>Commessa: {s.commessa_nome}</span>}
                    {s.tipo_spesa_nome && <span>Tipo: {s.tipo_spesa_nome}</span>}
                    {s.descrizione && <span className="italic">{s.descrizione}</span>}
                  </div>
                  {s.motivo_rifiuto && (
                    <p className="mt-1 text-xs text-red-600">Rifiuto: {s.motivo_rifiuto}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Upload ricevuta */}
                  {!s.receipt_file_path && s.status === 'inviata' && (
                    <label className="cursor-pointer p-1.5 hover:bg-blue-50 text-blue-600 rounded" title="Carica ricevuta">
                      <Upload size={15} />
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*,application/pdf"
                        onChange={e => {
                          const f = e.target.files?.[0]
                          if (f) handleUploadRicevuta(s.id, f)
                        }}
                        disabled={uploadingId === s.id}
                      />
                    </label>
                  )}
                  {s.receipt_file_path && (
                    <span className="text-green-600 text-xs flex items-center gap-1">
                      <FileText size={13} /> Ricevuta
                    </span>
                  )}
                  {/* Admin actions */}
                  {isAdmin && s.status === 'inviata' && (
                    <>
                      <button
                        onClick={() => approvaMut.mutate(s.id)}
                        className="p-1.5 hover:bg-green-50 text-green-600 rounded"
                        title="Approva"
                      >
                        <CheckCircle size={15} />
                      </button>
                      <button
                        onClick={() => { setRifiutoId(s.id); setMotivoRifiuto('') }}
                        className="p-1.5 hover:bg-red-50 text-red-600 rounded"
                        title="Rifiuta"
                      >
                        <XCircle size={15} />
                      </button>
                    </>
                  )}
                  {isAdmin && s.status === 'approvata' && (
                    <button
                      onClick={() => pagaMut.mutate(s.id)}
                      className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded"
                    >
                      Segna Pagata
                    </button>
                  )}
                  {(isTecnico || isAdmin) && ['inviata', 'rifiutata'].includes(s.status) && (
                    <button
                      onClick={() => confirmDelete('Eliminare questa spesa?', () => deleteMut.mutate(s.id))}
                      className="p-1.5 hover:bg-red-50 text-red-500 rounded"
                      title="Elimina"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
